import { FormEventHandler, PropsWithChildren, useState } from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import { SupportedLanguage } from '@/models/TranslatableField';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import { FeedbackFormTitle } from './FeedbackFormTitle';
import { QuestionFormTab, QuestionFormTabs } from './QuestionFormTabs';
import { QuestionFormTitle } from './QuestionFormTitle';
import { QuestionFeedbacksSection } from './feedbacks/QuestionFeedbacksSection';

type QuestionFormContainerProps<T extends FieldValues> = PropsWithChildren<{
  methods: UseFormReturn<T>;
  activeLanguage: SupportedLanguage;
  onLanguageChange: (lang: SupportedLanguage) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  isLoading?: boolean;
  hasChanges: boolean;
  onCancel: () => void;
}>;

export function QuestionFormContainer<T extends FieldValues>({
  methods,
  activeLanguage,
  onLanguageChange,
  onSubmit,
  isLoading,
  hasChanges,
  onCancel,
  children,
}: QuestionFormContainerProps<T>) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.questionForm',
  });

  const [activeTab, setActiveTab] = useState<QuestionFormTab>(
    QuestionFormTab.CONTENT,
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit}
        className="flex flex-1 flex-col gap-6 bg-base-200 rounded-2xl p-4"
      >
        <QuestionFormTabs value={activeTab} onChange={setActiveTab} />

        <ConditionalRenderer condition={activeTab === QuestionFormTab.CONTENT}>
          <QuestionFormTitle
            activeLanguage={activeLanguage}
            onLanguageChange={onLanguageChange}
          />
          {children}
        </ConditionalRenderer>

        <ConditionalRenderer condition={activeTab === QuestionFormTab.FEEDBACK}>
          <FeedbackFormTitle
            activeLanguage={activeLanguage}
            onLanguageChange={onLanguageChange}
          />
          <QuestionFeedbacksSection activeLanguage={activeLanguage} />
        </ConditionalRenderer>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            className="bg-base-300"
            color="default"
            onClick={onCancel}
            disabled={isLoading || !hasChanges}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            color="primary"
            disabled={isLoading || !hasChanges}
            loading={isLoading}
          >
            <ArrowDownTrayIcon className="size-3.5" />
            {t('save')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
